import React, { Fragment, useState } from 'react'

import { FiltersSidebar } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { array, func, object, number, string, oneOfType } from 'prop-types'

import WithTriangleUiContext from 'Pods/Shared/Hocs/WithTriangleUiContext'
import i18n from 'Pods/Shared/I18n'

import shortenValue from 'Utils/ShortenValue'
import { EMPTY_ARRAY } from 'Utils/stableDefaults'

import PolyCountSlider from './Components/PolyCountSlider'
import FiltersWrapperWithTags from '../../FiltersWrapperWithTags/FiltersWrapperWithTags'

function PolyCount({
  currentSelectedFilters,
  currentlySelectedFiltersObjects = EMPTY_ARRAY,
  initialFrom = null,
  initialTo = null,
  maxPolygonThreshold,
  minPolygonThreshold,
  setSelectedFilters,
  rangeFilters,
  setShouldResetPolygonRange,
  selectedFilters,
  placeholder,
  setSearchEnchantmentAction,
  trackFilterChange,
  locale = 'en',
}) {
  const [triggerCloseDropdown, setTriggerCloseDropdown] = useState(false)

  const quickSelectionsLabel = i18n.t('listingPage.filters.quickSelections', { ns: 'rails', lng: locale })

  function handleFilterChange(_, value, isOn, resetAll) {
    const selectedPolygonRangeFilters = selectedFilters.polygonRange || []

    if (resetAll) {
      setSearchEnchantmentAction({
        type: 'remove_filter',
        value: selectedPolygonRangeFilters.join(','),
      })
      setSelectedFilters({
        name: 'polygonRange',
        value: [],
        skipGetLastSelectedFilters: true,
      })
      setShouldResetPolygonRange(true)
      window.removeCurrentSearchFilter({ name: 'polygonRange' })

      return
    }

    const newPolygonRangeFilters = isOn
      ? [...selectedPolygonRangeFilters, value]
      : selectedPolygonRangeFilters.filter((item) => item !== value)
    setSelectedFilters({ name: 'polygonRange', value: newPolygonRangeFilters })

    if (isOn) {
      trackFilterChange({ name: 'polygonRange', value })
    } else {
      window.removeCurrentSearchFilter({ name: 'polygonRange', value })
    }

    const rangeFiltersList = rangeFilters.map((item) => item.value)
    if (!rangeFiltersList.includes(value)) {
      setShouldResetPolygonRange(true)
    }
  }

  function handleEnterKeyPress() {
    setTriggerCloseDropdown(true)
  }

  function getContent() {
    return (
      <Fragment>
        <PolyCountSlider
          initialFrom={initialFrom}
          initialTo={initialTo}
          maxPolygonThreshold={maxPolygonThreshold}
          minPolygonThreshold={minPolygonThreshold}
          setSelectedFilters={setSelectedFilters}
          handleEnterKeyPress={handleEnterKeyPress}
        />
        <FiltersSidebar
          isCollapsible={false}
          filters={[{ filters: rangeFilters, label: quickSelectionsLabel }]}
          selectedFilters={currentSelectedFilters}
          onFiltersChange={handleFilterChange}
          isControlled
          hideLastDivider
          useNewCheckboxes
          hideShowMore
        />
      </Fragment>
    )
  }

  return (
    <FiltersWrapperWithTags
      currentlySelectedFiltersObjects={currentlySelectedFiltersObjects || []}
      placeholder={placeholder}
      type="poly-count"
      content={getContent()}
      handleFilterChange={handleFilterChange}
      triggerCloseDropdown={triggerCloseDropdown}
      setTriggerCloseDropdown={setTriggerCloseDropdown}
    />
  )
}

PolyCount.propTypes = {
  currentSelectedFilters: array.isRequired,
  currentlySelectedFiltersObjects: array,
  initialFrom: oneOfType([string, number]),
  initialTo: oneOfType([string, number]),
  maxPolygonThreshold: number.isRequired,
  minPolygonThreshold: number.isRequired,
  setSelectedFilters: func.isRequired,
  rangeFilters: array.isRequired,
  setShouldResetPolygonRange: func.isRequired,
  selectedFilters: object.isRequired,
  placeholder: string.isRequired,
  setSearchEnchantmentAction: func.isRequired,
  trackFilterChange: func.isRequired,
  locale: string,
}

const mapStateToProps = ({
  selectedFilters,
  setSelectedFilters,
  filtersOptions,
  maxPolygonThreshold,
  minPolygonThreshold,
  setShouldResetPolygonRange,
  setSearchEnchantmentAction,
  trackFilterChange,
  locale,
}) => {
  const rangeFilters = filtersOptions.polygons.filters
  const placeholder = filtersOptions.polygons.label
  const rangeFiltersList = rangeFilters.map(({ value }) => value)
  const selectedPolygonRangeFilters = selectedFilters.polygonRange || []
  const customPolygonRange = selectedPolygonRangeFilters.find((value) => !rangeFiltersList.includes(value))
  const [initialFrom, initialTo] = (() => {
    if (customPolygonRange) {
      const splitValue = customPolygonRange.split('_').slice(1)
      return [splitValue[0], splitValue[1]]
    }

    return [undefined, undefined]
  })()
  const currentSelectedFilters = selectedPolygonRangeFilters.filter(Boolean)
  const currentlySelectedFiltersObjects = getSelectedFiltersObjects(currentSelectedFilters)

  function getSelectedFiltersObjects(filters = []) {
    const selectedFiltersObjects = filters
      .map((filter) => rangeFilters.find(({ value }) => value === filter))
      .filter(Boolean)

    if (initialFrom && initialTo) {
      selectedFiltersObjects.push({
        label: `${shortenValue(initialFrom)} - ${shortenValue(initialTo)}`,
        value: `range_${initialFrom}_${initialTo}`,
      })
      currentSelectedFilters.push(`range_${initialFrom}_${initialTo}`)
    }

    return selectedFiltersObjects
  }

  return {
    currentSelectedFilters,
    currentlySelectedFiltersObjects,
    initialFrom,
    initialTo,
    maxPolygonThreshold,
    minPolygonThreshold,
    setSelectedFilters,
    rangeFilters,
    setShouldResetPolygonRange,
    selectedFilters,
    placeholder,
    setSearchEnchantmentAction,
    trackFilterChange,
    locale,
  }
}

export default WithTriangleUiContext(mapStateToProps)(PolyCount)
